<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-boundary__content">
      <h3>Something went wrong</h3>
      <details class="error-boundary__details">
        <summary>Error Details (Click to expand)</summary>
        <div class="error-boundary__error-info">
          <p><strong>Error:</strong> {{ error?.message || 'Unknown error' }}</p>
          <p><strong>Component:</strong> {{ errorInfo?.componentName || 'Unknown' }}</p>
          <pre v-if="error?.stack" class="error-boundary__stack">{{ error.stack }}</pre>
        </div>
      </details>
      <button @click="retry" class="error-boundary__retry-btn">
        Try Again
      </button>
    </div>
  </div>
  <slot v-else />
</template>

<script setup>
import { ref, onErrorCaptured } from 'vue'

const hasError = ref(false)
const error = ref(null)
const errorInfo = ref(null)

onErrorCaptured((err, instance, info) => {
  try {
    console.error('Vue Error Boundary caught error:', {
      error: err,
      errorInfo: info,
      componentInstance: instance,
      stack: err?.stack
    })
    
    hasError.value = true
    error.value = err
    errorInfo.value = {
      componentName: instance?.$options?.name || instance?.$options?.__name || 'Anonymous',
      info
    }
    
    // Return false to prevent the error from propagating further
    return false
  } catch (boundaryError) {
    // Fallback error handling if the error boundary itself fails
    console.error('Error boundary failed:', boundaryError)
    hasError.value = true
    error.value = err || boundaryError
    errorInfo.value = {
      componentName: 'ErrorBoundary',
      info: 'Error boundary failure'
    }
    return false
  }
})

const retry = () => {
  hasError.value = false
  error.value = null
  errorInfo.value = null
}
</script>

<style scoped>
.error-boundary {
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
}

.error-boundary__content {
  max-width: 100%;
}

.error-boundary__content h3 {
  color: #c33;
  margin-bottom: 1rem;
}

.error-boundary__details {
  margin: 1rem 0;
}

.error-boundary__details summary {
  cursor: pointer;
  padding: 0.5rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.error-boundary__error-info {
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9rem;
}

.error-boundary__stack {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
  background-color: #fff;
  padding: 0.5rem;
  border-radius: 4px;
  margin-top: 0.5rem;
}

.error-boundary__retry-btn {
  background-color: #007cba;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 1rem;
}

.error-boundary__retry-btn:hover {
  background-color: #005a87;
}
</style>
